<template>
  <div
    class="dui-progress-steps"
    :class="vm.colorClass"
  >
    <div class="current-step step-text">
      {{ currentStep }}
    </div>
    <div class="steps">
      <span
        v-for="(step, index) in steps"
        :key="index"
        class="step-container"
        :class="{ 'active': (index < current) }"
        :style="{ 'width': stepWidth + '%' }"
      >
        <span class="step">
          <span class="step-text">{{ step }}</span>
        </span>
      </span>
    </div>

    <div
      ref="progress"
      class="progress"
      :style="{ width: progressWidth + '%' }"
    />

    <!-- Step progress -->
    <span
      v-if="hasStepProgressWidth"
      class="step-progress"
      :style="{ 'left': progressWidth + '%',
                'transform': 'translateX(-' + progressWidth + '%)' }"
    >{{ stepsProgress[current - 1] }}%</span>
    <!-- END Step progress -->
  </div>
</template>

<script>
import coloredComponent from '@dui/mixins/colored-component';
import SASS from '@dui/helpers/sass';

export default {
  mixins: [
    coloredComponent,
  ],
  props: {
    current: {
      type: Number,
      default: 1,
    },
    globalProgressOnSmScreens: {
      type: Boolean,
      default: false,
    },
    steps: {
      type: Array,
      required: true,
    },
    stepsProgress: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    currentStep() {
      return this.steps[this.current - 1];
    },
    hasStepProgressWidth() {
      if (!this.stepsProgress ||
        this.current === this.steps.length ||
        (typeof this.stepsProgress[this.current - 1] !== 'number')) {
        return false;
      }
      return true;
    },
    progressWidth() {
      if (this.current === 1 || this.shouldIgnoreFullProgress()) {
        return this.getStepProgressWidth();
      }

      if (this.current === this.steps.length) {
        return 100;
      }

      const progressWidth = this.stepWidth * (this.current - 1);
      return (progressWidth + this.getStepProgressWidth());
    },
    stepWidth() {
      const numSteps = this.steps.length;
      if (numSteps === 0) {
        return 0;
      }

      const stepWidth = (1 / ( numSteps - 1 )) * 100;
      return stepWidth;
    },
  },
  methods: {
    getStepProgressWidth() {
      if (!this.hasStepProgressWidth) {
        return 0;
      }

      if (this.shouldIgnoreFullProgress()) {
        return this.stepsProgress[this.current -1];
      }

      return this.stepWidth * (this.stepsProgress[this.current -1] / 100);
    },

    shouldIgnoreFullProgress() {
      return !this.globalProgressOnSmScreens &&
        (window.innerWidth <= SASS.screenSize.sm);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";

$step-inactive-color: getColor($grey, fade);
$step-marker-bottom: -($_2em + $_1em - ($border-width-base * 0.5));

@mixin makeColorProgressSteps($color,
                              $variation: base,
                              $progress-color: $white,
                              $progress-color-variation: base) {
  .progress {
    background-color: getColor($color, $variation);
  }

  .step-progress {
    background-color: getColor($color, $variation);
    color: getColor($progress-color, $progress-color-variation);
  }

  .step-container.active .step:after {
    background-color: getColor($color, $variation);
  }
}

.dui-progress-steps {
  padding-bottom: $_4em;
  position: relative;

  &:after,
  .progress {
    bottom: $_2em;
    content: " ";
    left: 0;
    height: $border-width-base;
    position: absolute;
  }

  &:after {
    background-color: $step-inactive-color;
    width: 100%;
  }

  .progress {
    width: 0;
    z-index: 1;

    @include transition((
      width
    ), $transition-time-slow);
  }

  .step-progress {
    border-radius: $border-radius-xl;
    bottom: $_1em;
    display: inline-block;
    font-size: $font-size-sm;
    font-weight: $base-weight-bold;
    height: 20px;
    line-height: 20px;
    padding: 0 $_1em;
    position: absolute;
    z-index: 3;

    @include transition((
      left,
      transform
    ), $transition-time-slow);

    &.close-to-finish {
      transform: translateX(-100%);
    }
  }

  .current-step.step-text {
    color: getColor($black);

    @include screenMoreThan(sm) {
      display: none;
    }
  }

  .step-text {
    color: getColor($grey, dark);
    font-weight: $base-weight-bold;
  }

  .steps {
    display: none;
    width: 100%;

    @include screenMoreThan(sm) {
      display: inline-block;
    }

    .step-container {
      display: inline-block;
      height: 1px;
      position: relative;

      @include transition((
        color,
      ), $transition-time-slow);

      &.active .step-text {
        color: getColor($black);

        @include transition((
          color,
        ), $transition-time-slow, $transition-time-base);
      }

      &:first-child {
        width: 0 !important;

        .step {
          left: 0;
          right: initial;
          transform: none;

          &:after {
            left: 0;
            transform: none;
          }
        }
      }
      &:last-child {
        .step {
          transform: none;

          &:after {
            left: initial;
            right: -1px;
            transform: none;
          }
        }
      }

      .step {
        bottom: -5px;
        position: absolute;
        right: 0;
        transform: translateX(50%);
        white-space: nowrap;
        z-index: 1;

        &:after {
          background-color: $step-inactive-color;
          bottom: $step-marker-bottom;
          border-radius: 50%;
          content: " ";
          height: $_2em;
          left: 50%;
          position: absolute;
          transform: translateX(-50%);
          width: $_2em;
          z-index: 2;

          @include transition((
            background-color,
          ), $transition-time-slow, $transition-time-base);
        }
      }
    }
  }

  // Color variants
  @include makeColorProgressSteps($yellow);
  &.color-yellow { @include makeColorProgressSteps($yellow); }
  &.color-black  { @include makeColorProgressSteps($black); }
  &.color-grey   { @include makeColorProgressSteps($grey, dark); }
  &.color-red    { @include makeColorProgressSteps($red); }
  &.color-sand   { @include makeColorProgressSteps($sand, medium, $black); }
  &.color-white  { @include makeColorProgressSteps($white, base, $black); }
  // END Color variants
}
</style>
